import React from "react";
import { Button } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import { useFormNavigation } from "@craft-form/renderer";
import useProposalBlueprint from "../hook/useProposalBlueprint";

// The Previous/Next/Finish row used when getViewMasterData returns
// `renderer: 1`. Same markup and same classes as the legacy row in
// membersDetails.js — only the wiring differs: navigation comes from
// @craft-form/renderer's own nav-controls API instead of the layout walking in
// hook/useProposalWizard.
//
// MUST be rendered inside <CraftFormProvider> — that is why membersDetails
// passes it to CraftProposalForm as children rather than rendering it beside
// the form. NavControlsProvider is mounted by CraftFormProvider, so calling
// useFormNavigation() outside it returns an inert default whose next/previous/
// finish all silently no-op.
//
// The controls come from the mounted `wizardMode: true` tabs layout, which
// publishes the very closures its own built-in buttons call — so Next runs the
// same current-tab-only validation, and Finish the same whole-form validation
// plus onSave, that the library's buttons would.

const ProposalNavButtons = ({ onExit, onJumpToStep }) => {
  // Per-tab field names and name->label come from the published blueprint.
  // These were module-level consts off a static JSON.js import; that is no
  // longer possible now the blueprint is fetched, since at module-evaluation
  // time it has not arrived yet.
  const { stepFieldNames: tabFieldNames, fieldLabels } = useProposalBlueprint();

  const {
    isFirstStep,
    isLastStep,
    nextDisabled,
    finishDisabled,
    next,
    previous,
    finish,
  } = useFormNavigation();

  const methods = useFormContext();

  // On the first step there is nowhere back to step to, so "Previous" leaves
  // the form entirely — same destination as the legacy path.
  const handlePrevious = () => {
    if (isFirstStep) {
      onExit();
      return;
    }
    previous();
  };

  // Finish validates the WHOLE form, and every tab is mounted at once (the
  // renderer's TabPanel hides inactive tabs with `display: none` rather than
  // unmounting them), so a required field left empty on ANY tab blocks the
  // submit. The library's tabs layout only console.logs that failure — unlike
  // its wizard layout it does not scroll to or focus the offending field — and
  // the field is usually on a tab you cannot currently see, so an unguided
  // Finish looks like a dead button. Surface it instead: say what is missing
  // and jump to the tab holding the first error.
  const handleFinish = async () => {
    const submitted = await finish();
    if (submitted) return;

    const invalidNames = Object.keys(methods.formState.errors || {});
    if (invalidNames.length === 0) {
      // Validation passed, so the save itself failed. updateForm already
      // toasts on a rejected request; this covers a non-200 falling through.
      toast.error("Could not submit the proposal. Please try again.");
      return;
    }

    const stepIndex = tabFieldNames.findIndex((names) =>
      names.some((name) => invalidNames.includes(name)),
    );
    if (stepIndex >= 0 && typeof onJumpToStep === "function") {
      onJumpToStep(stepIndex);
    }

    const labels = invalidNames.map((name) => fieldLabels[name] || name);
    const shown = labels.slice(0, 3).join(", ");
    toast.error(
      labels.length > 3
        ? `Please complete: ${shown} and ${labels.length - 3} more.`
        : `Please complete: ${shown}.`,
    );
  };

  // next() resolves false when the current tab fails validation. The inline
  // errors are already on screen; the toast just makes the block explicit so
  // Next never looks like a dead button. (Earlier-page failures are caught
  // and toasted by useProposalWizard's guarded setActiveStep.)
  const handleNext = async () => {
    const advanced = await next();
    if (!advanced) {
      toast.error("Please fill all required fields on this page before continuing.");
    }
  };

  return (
    <div className="navigation-buttons">
      <Button variant="primary previous" onClick={handlePrevious}>
        Previous
      </Button>
      {isLastStep ? (
        <Button
          variant="primary next"
          color="success"
          disabled={finishDisabled}
          onClick={handleFinish}
        >
          Finish
        </Button>
      ) : (
        <Button
          variant="primary next"
          color="primary"
          disabled={nextDisabled}
          onClick={handleNext}
        >
          Next
        </Button>
      )}
    </div>
  );
};

export default ProposalNavButtons;
